import { AccountInfo, OpenOrderReport, Portfolio, RegimeAnalytics, Trade, TradingClass } from './entities';
import {
  ScheduleState,
  StrategyType,
  LegOrderType,
  LegOrderAction,
  RaceHarvestType,
  MarketTradingHours,
  ProfitDirection,
  StrategyTaskStatus,
  VolatilityRegime,
  VolatilityRegimeStatus,
  BarSize,
  SpreadSide,
  RacingPhase,
  RedlineFormationStatus,
  RedlineRacingStatus,
  MarketPriceSide,
  SpreadMomentum,
  SpreadRegime,
  SlipstreamResult,
  ApexResult,
  OvertakeResult,
  PursuitResult,
  ChicaneRaceState
} from './enums';
import { ContractDetails } from './tws-entities';

export interface StrategyEvent {
  strategyId?: string | undefined;
  updatedTimestamp: Date | string | undefined;
  racingPhase: RacingPhase;
  scheduleState: ScheduleState;
  hasPosition: boolean;
  hasTimeout: boolean;
  sumUnrealizedPnl: number;
  profitTarget: number;
  errorMessage?: string | undefined;
  markedForStopLoss: boolean;
  marketTradingHours: MarketTradingHours | undefined;
}

export interface Strategy {
  id: string;
  type: StrategyType;
  created: string | Date;
  name: string;
  description?: string | undefined;
  scheduleState: ScheduleState;
  autoReopen: boolean;
  usePositionTimeout: boolean;
  useFullTimeoutLossRecovery: boolean;
  positionTimeout: number;
  useProfitTarget: boolean;
  profitTarget: number;
  useTrailingStopLoss: boolean;
  trailingStopLoss: number;
  sortOrder: number;
  debugMode: boolean;
  color?: string | undefined;
  raceCarColor?: string | undefined;
  accountCode?: string | undefined;
  useStopLoss: boolean;
  useFullStopLossRecovery: boolean;
  stopLossThreshold: number;
  stopLossRecovery: number;
  useStaleOrderRepair: boolean;

  // deprecated
  useComboOrder: boolean;
}

export interface Leg {
  id: string;
  strategyId: string;
  name: string;
  color?: string | undefined;
  details?: ContractDetails | undefined;
  useOption: boolean;
  optionDetails?: ContractDetails | undefined;
  optionTradingClass?: TradingClass | undefined;
  sortOrder: number;

  averageTrueRangeBarSize: number;
  averageTrueRangePeriods: number;
  averageTrueRange: number;
  averageTrueRangePnl: number;
  marginRequirement: number;
  marketValue: number;
  historicVolatility: number;
  impliedVolatility: number;

  orderType: LegOrderType;
  orderAction: LegOrderAction;
  orderSize: number;
  maxOrderSlippage: number;

  // deprecated - renamed
  //maxLimitSpreadLoss: number;

  // deprecated - moved to trade table
  trades?: Trade[] | undefined;
}

export interface LegEvent {
  id: string;
  legId?: string | undefined;
  strategyId?: string | undefined;
  updatedTimestamp: Date | string | undefined;
  racingPhase: RacingPhase;
  scheduleState: ScheduleState;
  marketTradingHours: MarketTradingHours;
  status: StrategyTaskStatus;
  marketPrice: number;
  theoreticalPositionSize: number;
  positionSize: number;
  positionSizeDiff: number;
  unrealizedPnl: number;
  sumUnrealizedPnl: number;
  profitDirection: ProfitDirection;
  profitTarget: number;
  profitTargetReached: boolean;
  orderAction?: 'BUY' | 'SELL' | undefined;
  orderSize: number;
  errorMessage?: string | undefined;
}

export interface RedlineStrategyEvent extends StrategyEvent {
  primarySideAction: LegOrderAction;
  counterSideAction: LegOrderAction;
  redlineOrderAction: LegOrderAction;
  primaryTrendEfficiencyPct?: number | undefined;
  counterTrendEfficiencyPct?: number | undefined;
  atrPnl: number;
  skewEntryMinAmount: number;
  redline: number;
  redlineRisk: number;
  microHarvestProfitTarget: number;
  previousRedlineStatus: RedlineFormationStatus;
  redlineStatus: RedlineFormationStatus;
  previousRedlineRaceStatus: RedlineRacingStatus;
  redlineRaceStatus: RedlineRacingStatus;
  raceStartedTimestamp?: Date | string | undefined;
  redlineBreakoutTimestamp?: Date | string | undefined;
  redlineStopLoss?: number;
  sumPrimarySideUpnl: number;
  sumCounterSideUpnl: number;
  sessionMaxLoss: number;
  sessionMaxLossPct: number;
  sessionMaxProfit: number;
  sessionMaxProfitPct: number;
  doubleUp: boolean;
  skewPrimarySideAction?: LegOrderAction | undefined;
  blockEntryMessage: string | undefined;
  spreadTrendEfficienyPct: number | undefined;
  primaryTrendEfficienyPct: number | undefined;
  counterTrendEfficienyPct: number | undefined;
  weightedZScore: number;
  zScoreMomentum: SpreadMomentum;

  correction1Size: number;
  correction1Status?: string | undefined;
  correction2Size: number;
  correction2Status?: string | undefined;
  correction3Size: number;
  correction3Status?: string | undefined;
  correction4Size: number;
  correction4Status?: string | undefined;

  // deprecated
  redlineProfitTarget?: number;
  skewEntryMessage?: number | undefined;
}

export type PreTradeIndicatorsState = 'IDEAL' | 'DEAD' | 'TRENDING' | 'DRIFTING';

export interface PreTradeIndicators {
  // latest underlying bar timestamp used (from the 5m series)
  dataTimestamp: string; // ISO date

  // when backend computed the response
  timestamp: string; // ISO date

  ltBetaPct: number; // signed percent; + primary heavier, - counter heavier
  ltBetaConfigured: boolean; // false when beta reference/values missing
  ltBetaMessage?: string | null;

  stBetaPct: number; // signed percent; + primary heavier, - counter heavier
  stBetaConfigured: boolean; // false when beta reference/values missing
  stBetaMessage?: string | null;

  ltTePct: number; // signed percent (1h bars × ~160)
  ltSlowTePct: number;
  spreadRegime: SpreadRegime;
  stTePct: number; // signed percent (5m bars × ~48)
  firstSpreadPnl: number;
  stTeSecondPct: number; // signed percent (5m bars × ~48) - 10 periods behind
  secondSpreadPnl: number;
  stTeThirdPct: number; // signed percent (5m bars × ~48) - 20 periods behind

  stZScore: number; // signed z-score (5m bars × ~48)

  stTeMomentum: SpreadMomentum | undefined;
  stWeightedZScore: number | undefined;

  // depracated - renamed
  betaPct: number;
  betaConfigured: boolean;
  betaMessage?: string | null;

  // deprecated
  state: PreTradeIndicatorsState;
}

export interface RedlineStrategy extends Strategy {
  primarySideAlias: string;
  counterSideAlias: string;
  primaryOrderAction: LegOrderAction;
  showSpreadHistory: boolean;
  showPreTradeIndicators: boolean;
  preTradeIndicators?: PreTradeIndicators | null;
  betaAtrBarSize: BarSize;
  betaAtrPeriods: number;
  atrBarSize: BarSize;
  atrPeriods: number;
  atrRedlineMultiplier: number;
  atrProfitMultiplier: number;
  useTrailingStopLoss: boolean;
  useMicroHarvest: boolean;
  microHarvestProfitTarget: number;
  microHarvestProfitMultiplier: number;
  trendEfficiencyThreshold: number;
  trendEfficiencyBarSize: BarSize;
  trendEfficiencyPeriods: number;
  enableRedlineRacing: boolean;
  redlineRisk: number;
  redlineRiskMultiplier: number;
  useRedlineTeGate: boolean;
  redlineTeBarSize: BarSize;
  redlineTePeriods: number;
  useSkewEntry: boolean;
  skewEntryMinAmount: number;
  skewEntryMultiplier: number;
  confirmSkewFade: boolean;
  useResetFormationOnLoss: boolean;
  doubleUp: boolean;
  autoDoubleUp: boolean;
  doubleUpMaxLoss: number;
  doubleUpMaxLossPct: number;
  useMaxSpreadTeEntry: boolean;
  maxSpreadTePct: number;
  restrictOpeningHoursEntry: boolean;
  openingHoursStart: string | undefined;
  openingHoursEnd: string | undefined;
  useZScoreEntry: boolean;
  zScoreBarSize: BarSize;
  zScorePeriods: number;
  zScoreTePeriods: number;
  zScoreThreshold: number;

  correction1Enabled: boolean;
  correction1LegId?: string | undefined;
  correction1Size: number;
  correction1MinTe: number;
  correction1MaxTe: number;
  correction1EnterMlt: number;
  correction1ExitMlt: number;

  correction2Enabled: boolean;
  correction2LegId?: string | undefined;
  correction2Size: number;
  correction2MinTe: number;
  correction2MaxTe: number;
  correction2EnterMlt: number;
  correction2ExitMlt: number;

  correction3Enabled: boolean;
  correction3LegId?: string | undefined;
  correction3Size: number;
  correction3MinTe: number;
  correction3MaxTe: number;
  correction3EnterMlt: number;
  correction3ExitMlt: number;

  correction4Enabled: boolean;
  correction4LegId?: string | undefined;
  correction4Size: number;
  correction4MinTe: number;
  correction4MaxTe: number;
  correction4EnterMlt: number;
  correction4ExitMlt: number;

  latestEvent?: RedlineStrategyEvent | undefined;
  // logs: RedlineStrategyEvent[];

  // deprecated
  useZScoreTrendEntry: boolean;
  skewEntryMaxTe: number;
  skewEntryLookbackPeriods: number;
}

export interface RedlineLeg extends Leg {
  spreadSide: SpreadSide;
  betaReference?: ContractDetails | undefined;
  betaReferenceValue: number;
  betaWeightedValue: number;
  latestEvent?: RedlineLegEvent | undefined;
  // logs: RedlineLegEvent[];
}

export interface RedlineLegEvent extends LegEvent {
  initSize: number;
  correctionSize: number;
  correctionTriggered: boolean;
  correctionLoss: number;
  cutOffLoss: number;
  swapPnl: number;
  redline: number;
  redlineOrderAction: LegOrderAction;
  previousRedlineStatus: RedlineFormationStatus;
  redlineStatus: RedlineFormationStatus;
  redlineRaceStatus: RedlineRacingStatus;
  racingLeg: boolean;
  stopTriggerPrice: number;
  stopOrderSize: number;
  stopOrderAction: string | undefined;
  doubleUp: boolean;
}

export interface HotLapStrategyEvent extends StrategyEvent {
  lapAttempts: number;
  averageTrueRange: number | undefined;
  recentAverageTrueRange: number | undefined;
  regime: VolatilityRegime;
  regimeStatus: VolatilityRegimeStatus;
  trailingActivated: boolean;
  hasContinuedTrend: boolean;
  hasEntrySignal: boolean;
  raceStartedTimestamp: string | Date | undefined;
  entryPrice: number | undefined;
  marketPriceSide: MarketPriceSide;
  marketPrice: number;
  raceAttemptHigh?: number | undefined;
  raceAttemptLow?: number | undefined;
  stopLossPrice: number;
  stopLossAmount: number;
}

export interface HotLapStrategy extends Strategy {
  atrBarSize: BarSize;
  atrPeriods: number;
  recentAtrPeriods: number;
  atrHighThresholdPct: number;
  maxAttempts: number;
  maxPullbackAtr: number;
  trailingActivationAtr: number;
  trailingProfitPct: number;
  stopLossAtr: number;
  minimumStopLossAmount: number;

  latestEvent?: HotLapStrategyEvent | undefined;
  // logs: HotLapStrategyEvent[];

  // deprecated
  raceHarvestType: RaceHarvestType;
  minimumStopLossPnl: number;
}

export interface HotLapLegEvent extends LegEvent {
  initSize: number;
  stopLossPrice: number;
  stopLossAmount: number;

  // deprecated
  lapAttempts: number;
  profitMultiplier: number;
  regime: VolatilityRegime;
  regimeStatus: VolatilityRegimeStatus;
}

export interface HotLapLeg extends Leg {
  latestEvent?: HotLapLegEvent | undefined;
  // logs: HotLapLegEvent[];

  // deprecated
  recentAverageTrueRange: number;
  recentAverageTrueRangePnl: number;
  lossRange: number;
  stopLoss: number;
}

export interface SlipstreamStrategyEvent extends StrategyEvent {
  positionSize: number;
  averageCost: number;
  unrealizedPnl: number;
  realizedPnl: number;
  sessionMaxProfit: number;
  sessionMaxLoss: number;
  sessionStopLoss: number;
  sessionProfitTarget: number;
  remainingProfitPct?: number | undefined;
  result: SlipstreamResult;

  // independent TE confirmation gate value - fed by trendEfficiencyBarSize/trendEfficiencyPeriods
  trendEfficiencyPct: number;
  atrPnl: number;
  highestTrPnl: number;

  // hypothetical-long PnL over the Fast/Slow windows - the primary entry conflict gate (opposite signs
  // required to enter; direction taken from slowPnl's sign, magnitude of fastPnl floored by
  // minPullbackAtrMultiplier)
  fastPnl: number;
  slowPnl: number;

  // raw price-unit ATR from the moving ATR window - price-unit counterpart to atrPnl ($)
  movingAtr: number;

  // reason entry was blocked this cycle, or undefined if it passed / wasn't evaluated - same idiom as
  // RedlineStrategyEvent.blockEntryMessage
  blockEntryMessage?: string | undefined;

  orderAction?: 'BUY' | 'SELL' | undefined;
  hasEntry: boolean;

  tradingHours?: string | undefined;
  liquidHours?: string | undefined;

  // null until UseTrailingStopLoss has armed - the ratcheted PnL level the trailing STP sits at,
  // recomputed every cycle as a percentage of the current sessionMaxProfit
  trailingStopLoss?: number | undefined;

  // deprecated
  hasExit: boolean;
  slowTrendEfficiencyPct: number;
  drawdownTriggerPnl?: number | undefined;
  drawdownExitTriggered: boolean;
  entryTrendEfficiencyPct: number;
  entryAtrPnl: number;
  entryMaxTrPnl: number;
  ema: number;
  entryDistance: number;
  upperEntryLevel: number;
  lowerEntryLevel: number;
  currentPrice: number;
}

export interface SlipstreamStrategy extends Strategy {
  raceCarName: string;
  raceCarColor: string;
  raceCarDetails?: ContractDetails | undefined;
  orderSize: number;
  shortable: boolean;

  primaryOrderAction: LegOrderAction;
  useSpreadPnlEntry: boolean;

  // manual Average True Range Analysis tool - helps pick atrMinPnl/atrMaxPnl values by computing the
  // current ATR PnL for a given bar size/periods. Not read by the live entry-gating waterfall itself.
  atrBarSize: BarSize;
  atrPeriods: number;
  atrValue: number;
  atrPnl: number;

  // fast window - drives fastPnl (fast side of the entry conflict gate) and, with the moving ATR window
  // below, the pullback-magnitude floor (minPullbackAtrMultiplier)
  fastBarSize: BarSize;
  fastPeriods: number;

  // slow window - drives slowPnl (slow side of the entry conflict gate; direction from its sign)
  slowBarSize: BarSize;
  slowPeriods: number;

  // floor on how large the fast-side pullback has to be before it counts as a real conflict, not just
  // noise - Math.abs(fastPnl) must be at least this many moving-ATR units
  minPullbackAtrMultiplier: number;

  // moving ATR - the live, cycle-by-cycle ATR window driving movingAtr/stop/target sizing and the
  // pullback-magnitude floor above. Deliberately separate from both the manual analysis-tool
  // atrBarSize/atrPeriods above and the fast/slow windows above.
  movingAtrBarSize: BarSize;
  movingAtrPeriods: number;
  atrMinPnl: number;
  atrMaxPnl: number;
  atrStopLossMultiplier: number;
  atrProfitTargetMultiplier: number;

  // optional extra filter - applies atrMinPnl/atrMaxPnl + no-spike checks (both from the moving ATR
  // window) before the fast/slow conflict is evaluated; defaults true server-side for pre-existing
  // documents
  useAtrRangeGate: boolean;

  // optional Trend Efficiency confirmation gate - when true, blocks entry unless trendEfficiencyPct is
  // at least as extreme, in the direction implied by slowPnl's sign, as minTrendEfficiencyPct below (a
  // floor with direction alignment, not a ceiling). trendEfficiencyPctThreshold is a separate,
  // already-inert field, not read by this gate.
  useTrendEfficiency: boolean;
  trendEfficiencyBarSize: BarSize;
  trendEfficiencyPeriods: number;
  trendEfficiencyPctThreshold: number;

  // floor used by the useTrendEfficiency gate above
  minTrendEfficiencyPct: number;

  // trailing-stop distance once useTrailingStopLoss (inherited from Strategy) has armed - recomputed
  // every cycle as a percentage of the current sessionMaxProfit
  trailingStopMaxProfitPct: number;

  // caps the trailing-stop distance at a flat dollar amount, whichever is smaller against the
  // percentage-of-peak distance. 0 means unconfigured, falling back to the percentage alone.
  trailingStopMaxAmount: number;

  latestEvent?: SlipstreamStrategyEvent | undefined;

  // deprecated
  entryBarSize: BarSize;
  entryPeriods: number;
  teMinPct: number;
  teExitPct: number;
  atrExitPnl: number;
  microHarvestProfit: number;
  trailingStopAtrMultiplier: number;
  drawdownPct: number;
  drawdownMinProfit: number;
  emaBarSize: BarSize;
  emaPeriods: number;
  entryAtrMultiplier: number;
  maxTrendEfficiencyPct: number;
}

export interface ApexStrategyEvent extends StrategyEvent {
  positionSize: number;
  averageCost: number;
  unrealizedPnl: number;
  realizedPnl: number;
  sessionMaxProfit: number;
  sessionMaxLoss: number;
  sessionStopLoss: number;
  sessionProfitTarget: number;
  remainingProfitPct?: number | undefined;
  result: ApexResult;

  // independent TE confirmation gate value - fed by trendEfficiencyBarSize/trendEfficiencyPeriods
  trendEfficiencyPct: number;
  atrPnl: number;
  highestTrPnl: number;

  // fastPnl/slowPnl are unused - were the two sides of the entry conflict gate, fully removed in
  // Apex 3.0, no longer computed
  fastPnl: number;
  slowPnl: number;

  // raw price-unit ATR from the moving ATR window - price-unit counterpart to atrPnl ($)
  movingAtr: number;

  // EMA mean-reversion entry diagnostics (3.0)
  ema: number;
  entryDistance: number;
  upperEntryLevel: number;
  lowerEntryLevel: number;

  // last close from the EMA quote window - used as "current price" for the band comparison instead of
  // a separate live bid/ask fetch
  currentPrice: number;

  // reason entry was blocked this cycle, or undefined if it passed / wasn't evaluated - same idiom as
  // RedlineStrategyEvent.blockEntryMessage
  blockEntryMessage?: string | undefined;

  orderAction?: 'BUY' | 'SELL' | undefined;
  hasEntry: boolean;

  tradingHours?: string | undefined;
  liquidHours?: string | undefined;

  // null until UseTrailingStopLoss has armed - the ratcheted PnL level the trailing STP sits at,
  // recomputed every cycle as a percentage of the current sessionMaxProfit
  trailingStopLoss?: number | undefined;

  // deprecated
  hasExit: boolean;
  slowTrendEfficiencyPct: number;
  drawdownTriggerPnl?: number | undefined;
  drawdownExitTriggered: boolean;
  entryTrendEfficiencyPct: number;
  entryAtrPnl: number;
  entryMaxTrPnl: number;
}

export interface ApexStrategy extends Strategy {
  raceCarName: string;
  raceCarColor: string;
  raceCarDetails?: ContractDetails | undefined;
  orderSize: number;
  shortable: boolean;

  primaryOrderAction: LegOrderAction;
  useSpreadPnlEntry: boolean;

  // manual Average True Range Analysis tool - helps pick atrMinPnl/atrMaxPnl values by computing the
  // current ATR PnL for a given bar size/periods. Not read by the live entry-gating waterfall itself.
  atrBarSize: BarSize;
  atrPeriods: number;
  atrValue: number;
  atrPnl: number;

  // fast/slow windows - unused. Were the entry conflict gate's two sides, fully removed in Apex
  // 3.0 (superseded by the Moving ATR window below, a deliberately separate config). No longer computed.
  fastBarSize: BarSize;
  fastPeriods: number;
  slowBarSize: BarSize;
  slowPeriods: number;

  // EMA mean-reversion entry window (3.0) - drives ema/entryDistance/band levels. The last close of
  // this same quote window also doubles as currentPrice for the band comparison.
  emaBarSize: BarSize;
  emaPeriods: number;

  // moving ATR (3.0) - the live, cycle-by-cycle ATR window driving movingAtr/entryDistance and
  // stop/target sizing. Deliberately separate from both the manual analysis-tool atrBarSize/atrPeriods
  // above and the now-legacy fastBarSize/fastPeriods/slowBarSize/slowPeriods above.
  movingAtrBarSize: BarSize;
  movingAtrPeriods: number;
  atrMinPnl: number;
  atrMaxPnl: number;
  atrStopLossMultiplier: number;
  atrProfitTargetMultiplier: number;

  // entryDistance = movingAtr (raw price-unit ATR from the moving ATR window) * entryAtrMultiplier
  entryAtrMultiplier: number;

  // optional extra filter (3.0) - applies atrMinPnl/atrMaxPnl + no-spike checks (both from the moving
  // ATR window) before the EMA band is evaluated; defaults true server-side for pre-3.0 documents
  useAtrRangeGate: boolean;

  // optional Trend Efficiency ceiling gate (3.0) - when true, blocks entry while |trendEfficiencyPct|
  // exceeds maxTrendEfficiencyPct below. trendEfficiencyPctThreshold is no longer read (the old
  // direction-confirmation floor it fed is gone), kept for a possible rollback
  useTrendEfficiency: boolean;
  trendEfficiencyBarSize: BarSize;
  trendEfficiencyPeriods: number;
  trendEfficiencyPctThreshold: number;

  // ceiling used by the useTrendEfficiency gate above
  maxTrendEfficiencyPct: number;

  // trailing-stop distance once useTrailingStopLoss (inherited from Strategy) has armed - recomputed
  // every cycle as a percentage of the current sessionMaxProfit
  trailingStopMaxProfitPct: number;

  // caps the trailing-stop distance at a flat dollar amount, whichever is smaller against the
  // percentage-of-peak distance. 0 means unconfigured, falling back to the percentage alone.
  trailingStopMaxAmount: number;

  latestEvent?: ApexStrategyEvent | undefined;

  // deprecated
  entryBarSize: BarSize;
  entryPeriods: number;
  teMinPct: number;
  teExitPct: number;
  atrExitPnl: number;
  microHarvestProfit: number;
  trailingStopAtrMultiplier: number;
  drawdownPct: number;
  drawdownMinProfit: number;
}

export interface PitlaneStrategyEvent extends StrategyEvent {
  raceStartedTimestamp?: Date | string | undefined;
  primaryOrderAction: LegOrderAction;
  fastSpreadPnl: number;
  slowSpreadPnl: number;
  pitlaneThresholdMultiplier: number;
  currentPitlaneLossThreshold: number;
  sumRealizedPnl: number;
  sumSessionPnl: number;
  maxSessionProfit: number;
  maxSessionLoss: number;
  exitLoserIsPrimary?: boolean | undefined;
}

export interface PitlaneStrategy extends Strategy {
  primaryOrderAction: LegOrderAction;
  fastBarSize: BarSize;
  fastPeriods: number;
  slowBarSize: BarSize;
  slowPeriods: number;
  useSpreadPnlEntry: boolean;
  useFastSpreadPnl: boolean;
  spreadPnlThreshold: number;
  usePitlane: boolean;
  pitlaneLossThreshold: number;
  limitCorridorPnl: number;
  stopCorridorPnl: number;
  maxEntrySpreadPnl: number;
  betaAtrBarSize: BarSize;
  betaAtrPeriods: number;

  latestEvent?: PitlaneStrategyEvent | undefined;
}

export interface PitlaneLeg extends Leg {
  spreadSide: SpreadSide;
  fullExposureSize: number;
  pitlaneExposureSize: number;
  betaReference?: ContractDetails | undefined;
  betaReferenceValue: number;
  betaWeightedValue: number;
}

export interface OvertakeStrategyEvent extends StrategyEvent {
  raceStartedTimestamp?: Date | string | undefined;
  primaryOrderAction: LegOrderAction;
  fastSpreadPnl: number;
  slowSpreadPnl: number;
  sumRealizedPnl: number;
  sumSessionPnl: number;
  maxSessionProfit: number;
  maxSessionLoss: number;
  trailingStopLoss?: number | undefined;
  exitLoserIsPrimary?: boolean | undefined;
  overtakeMode: boolean;
  overtakeResult: OvertakeResult;
}

export interface OvertakeStrategy extends Strategy {
  primaryOrderAction: LegOrderAction;
  fastBarSize: BarSize;
  fastPeriods: number;
  slowBarSize: BarSize;
  slowPeriods: number;
  useSpreadPnlEntry: boolean;
  limitCorridorPnl: number;
  stopCorridorPnl: number;
  maxEntrySpreadPnl: number;
  betaAtrBarSize: BarSize;
  betaAtrPeriods: number;
  useOvertake: boolean;

  latestEvent?: OvertakeStrategyEvent | undefined;
}

export interface OvertakeLeg extends Leg {
  spreadSide: SpreadSide;
  betaReference?: ContractDetails | undefined;
  betaReferenceValue: number;
  betaWeightedValue: number;
}

export interface PursuitStrategyEvent extends StrategyEvent {
  raceStartedTimestamp?: Date | string | undefined;
  primaryOrderAction: LegOrderAction;
  trendEfficiencyPct?: number | undefined;
  trendAccelerationPct?: number | undefined;
  sumRealizedPnl: number;
  sumSessionPnl: number;
  maxSessionProfit: number;
  maxSessionLoss: number;
  trailingStopLoss?: number | undefined;
  exitLoserIsPrimary?: boolean | undefined;
  blockEntryMessage?: string | undefined;
  pursuitResult: PursuitResult;
  pursuitCarPnl?: number | undefined;
  laggingCarPnl?: number | undefined;
}

export interface PursuitStrategy extends Strategy {
  primaryOrderAction: LegOrderAction;
  trendEfficiencyBarSize: BarSize;
  trendEfficiencyPeriods: number;
  useTeGate: boolean;
  minTePct: number;
  useTeAccGate: boolean;
  minTeAccPct: number;
  limitCorridorPnl: number;
  stopCorridorPnl: number;
  maxEntrySpreadPnl: number;
  betaAtrBarSize: BarSize;
  betaAtrPeriods: number;
  trailingStopMaxProfitPct: number;
  trailingStopMaxAmount: number;
  showRecordingPanel: boolean;
  showSpreadHistoryPanel: boolean;
  historyCarRaceCarColor?: string | undefined;
  historyBarSize: BarSize;
  historyPeriods: number;
  pursuitCar: PursuitCar;
  laggingCar: LaggingCar;

  latestEvent?: PursuitStrategyEvent | undefined;
}

export interface PursuitHistoryPoint {
  time: Date | string;
  buyPnl: number;
  sellPnl: number;
}

export interface PursuitCar {
  raceCarColor?: string | undefined;
  delayMinutes: number;
  recording?: PursuitCarRecording | undefined;
}

export interface LaggingCar {
  useLaggingCar: boolean;
  raceCarColor?: string | undefined;
  lagMinutes: number;
}

export interface PursuitCarRecording {
  recordingStartedTimestamp: Date | string;
  legs: PursuitCarRecordingLeg[];
}

export interface PursuitCarRecordingLeg {
  legId?: string | undefined;
  conId: number;
  legName?: string | undefined;
  spreadSide: SpreadSide;
  orderSize: number;
  bidPrice: number;
  askPrice: number;
}

export interface PursuitLeg extends Leg {
  spreadSide: SpreadSide;
  betaReference?: ContractDetails | undefined;
  betaReferenceValue: number;
  betaWeightedValue: number;
}

export interface ChicaneStrategyEvent extends StrategyEvent {
  raceStartedTimestamp?: Date | string | undefined;
  primaryOrderAction: LegOrderAction;
  chicaneRaceState: ChicaneRaceState;
  sumRealizedPnl: number;
  primaryRealizedPnl: number;
  counterRealizedPnl: number;
  sumSessionPnl: number;
  maxSessionProfit: number;
  maxSessionLoss: number;
  primaryPosition: number;
  counterPosition: number;
  primaryPnl: number;
  counterPnl: number;
  primaryPnlAtHedge?: number | undefined;
  counterPnlAtHedge?: number | undefined;
  releaseSide?: SpreadSide | undefined;
  releasePnl: number;
  rehedgeAnchorPrice?: number | undefined;
  rehedgeTriggerPrice?: number | undefined;
  nextHedgePnl?: number | undefined;
  trendEfficiencyPct?: number | undefined;
  entryTrendEfficiencyPct?: number | undefined;
  displacementPrice?: number | undefined;
  displacementPnl?: number | undefined;
  displacementTimestamp?: Date | string | undefined;
}

export interface ChicaneStrategy extends Strategy {
  primaryOrderAction: LegOrderAction;
  hedgeProfitTarget: number;
  limitCorridorPnl: number;
  stopCorridorPnl: number;
  hedgeEntryLossBuffer: number;
  useHedgeEntryProfitBuffer: boolean;
  hedgeEntryProfitBuffer: number;
  maxEntrySpreadPnl: number;
  usePriceDisplacementEntry: boolean;
  displacementBarSize: BarSize;
  displacementMaxPeriods: number;
  minDisplacementLoss: number;
  useTrendEfficiency: boolean;
  trendEfficiencyBarSize: BarSize;
  trendEfficiencyPeriods: number;
  trendEfficiencyMinPctThreshold: number;
  useTrendEfficiencyReleaseGate: boolean;
  trendEfficiencyMaxPctThreshold: number;

  latestEvent?: ChicaneStrategyEvent | undefined;
}

// Primary = front month (Hedge), Counter = next calendar month (Core)
export interface ChicaneLeg extends Leg {
  spreadSide: SpreadSide;
}

export interface Dashboard {
  accountInfo: AccountInfo;
  errorMessage: string | undefined;
  openOrders: OpenOrderReport[];
  portfolios: Portfolio[];
  regimeAnalytics: RegimeAnalytics;
  sumPnlToday: number;
  averagePnl30Days: number;
  strategyLatestEvents: StrategyEvent[];
}

export function isRedlineStrategy(s: Strategy | undefined): s is RedlineStrategy {
  return !!s && s.type === StrategyType.Redline;
}

export function isHotLapStrategy(s: Strategy | undefined): s is HotLapStrategy {
  return !!s && s.type === StrategyType.HotLap;
}

export function isHotLapLeg(type: StrategyType, leg: Leg): leg is HotLapLeg {
  return type === StrategyType.HotLap;
}

export function isRedlineLeg(type: StrategyType, leg: Leg): leg is RedlineLeg {
  return type === StrategyType.Redline;
}

export function isSlipstreamStrategy(s: Strategy | undefined): s is SlipstreamStrategy {
  return !!s && s.type === StrategyType.Slipstream;
}

export function isApexStrategy(s: Strategy | undefined): s is ApexStrategy {
  return !!s && s.type === StrategyType.Apex;
}

export function isPitlaneStrategy(s: Strategy | undefined): s is PitlaneStrategy {
  return !!s && s.type === StrategyType.Pitlane;
}

export function isPitlaneLeg(type: StrategyType, leg: Leg): leg is PitlaneLeg {
  return type === StrategyType.Pitlane;
}

export function isOvertakeStrategy(s: Strategy | undefined): s is OvertakeStrategy {
  return !!s && s.type === StrategyType.Overtake;
}

export function isOvertakeLeg(type: StrategyType, leg: Leg): leg is OvertakeLeg {
  return type === StrategyType.Overtake;
}

export function isPursuitStrategy(s: Strategy | undefined): s is PursuitStrategy {
  return !!s && s.type === StrategyType.Pursuit;
}

export function isPursuitLeg(type: StrategyType, leg: Leg): leg is PursuitLeg {
  return type === StrategyType.Pursuit;
}

export function isChicaneStrategy(s: Strategy | undefined): s is ChicaneStrategy {
  return !!s && s.type === StrategyType.Chicane;
}

export function isChicaneLeg(type: StrategyType, leg: Leg): leg is ChicaneLeg {
  return type === StrategyType.Chicane;
}
